import {
  Address,
  AddressTypeEnum,
  EntityTypeEnum,
  UpdateWorkspaceDetailsDto,
} from '@sr-sdks/api-sdk-axios';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import {
  cleanObjForForm,
  workspaceTypeFromEntityType,
} from '../../../utils/helpers/common';
import { ADDRESS_GROUP_FIELDS, addressGroupHasContent } from './addressGroup';
import { normalizeCountryToIso2 } from './normalizeCountry';
import { WorkspaceDetailsFormValues } from './types';

/** react-hook-form's `formState.dirtyFields` for the Details form. */
export type WorkspaceDetailsDirtyFields = Partial<
  Readonly<FieldNamesMarkedBoolean<WorkspaceDetailsFormValues>>
>;

/**
 * The backend's UpdateWorkspaceDetailsDto enforces @ArrayMaxSize(2) on
 * addresses — one street entry plus at most one preserved non-street entry.
 */
const MAX_PRESERVED_ADDRESSES = 1;

/**
 * Map the Details form values onto the partial workspace-update DTO.
 *
 * Emits the entity subset plus the Contact (APP-1828) and Address (APP-1829)
 * sections — the backend shallow-merges, so unsent sections (bank) are
 * untouched. Individuals blank out the business number + verification,
 * mirroring the legacy submit.
 *
 * `contacts`/`phone`/`website`/`addresses` are gated on `dirtyFields`: the
 * form defaults are snapshotted once at mount, so re-sending untouched
 * sections would overwrite concurrent edits by other users via the backend's
 * shallow merge (last-write-wins). An entity-only save must leave them out of
 * the payload.
 *
 * The backend replaces the whole addresses array, so when the section is
 * sent, entries the form does not edit are carried through from
 * `existingAddresses` (required — omitting it would wipe stored postal
 * entries). The street entry keeps its stored line2 only while line1 is
 * unchanged — a replaced address (e.g. via Google Places) must not carry the
 * old premises' unit number. A dirty-but-blank address group is not sent at
 * all: this form cannot delete an address, and an all-empty street entry
 * would 400 against backend validation.
 */
export const toUpdateWorkspaceDetailsDto = (
  values: WorkspaceDetailsFormValues,
  dirtyFields: WorkspaceDetailsDirtyFields,
  existingAddresses: Address[],
  { isOnboarding = false }: { isOnboarding?: boolean } = {},
): UpdateWorkspaceDetailsDto => {
  const isIndividual = values.entityType === EntityTypeEnum.Individual;
  const existingStreet = existingAddresses.find(
    (address) => address.addressType === AddressTypeEnum.Street,
  );
  const addressDirty = ADDRESS_GROUP_FIELDS.some((field) =>
    Boolean(dirtyFields[field]),
  );
  /**
   * Onboarding sends every section regardless of dirty state. The dirty gate
   * exists to stop an entity-only settings save from clobbering another
   * editor's concurrent contact/address edits via the backend's shallow
   * merge — a workspace still being onboarded has no other editors, and the
   * legacy onboarding form posted the full payload too.
   */
  const sendAddresses =
    (isOnboarding || addressDirty) &&
    addressGroupHasContent(ADDRESS_GROUP_FIELDS.map((field) => values[field]));
  const contacts = cleanObjForForm(values.contacts);
  // dirtyFields can hold `false` entries for fields edited back to their
  // default, so check for a truthy flag rather than key presence.
  const contactsDirty = Object.values(dirtyFields.contacts ?? {}).some(Boolean);
  const sendContacts = isOnboarding || contactsDirty;

  return {
    abnStatus: values.abnStatus ?? undefined,
    ...(sendAddresses
      ? {
          addresses: [
            {
              addressType: AddressTypeEnum.Street,
              city: values.addressCity,
              country: values.addressCountry,
              line1: values.addressLine1,
              // line2 (unit/apartment) belongs to the premises identified by
              // line1 — keep it for in-place edits, drop it when the address
              // was replaced (no UI edits line2).
              line2:
                values.addressLine1 === existingStreet?.line1
                  ? (existingStreet?.line2 ?? '')
                  : '',
              postcode: values.addressPostcode,
              state: values.addressState,
            },
            // Preserved entries get best-effort ISO-2 normalization — a
            // resolvable legacy country ('New Zealand', 'nz') on a stored
            // postal address would otherwise 400 the whole PATCH against
            // @IsISO31661Alpha2. Unresolvable values are kept verbatim
            // rather than blanked: the user has no field to fix a postal
            // entry, and '' is just as invalid to the backend.
            ...existingAddresses
              .filter(
                (address) => address.addressType !== AddressTypeEnum.Street,
              )
              .slice(0, MAX_PRESERVED_ADDRESSES)
              .map((address) => ({
                ...address,
                country:
                  normalizeCountryToIso2(address.country) || address.country,
              })),
          ],
        }
      : {}),
    businessNumber: isIndividual ? '' : (values.businessNumber ?? ''),
    businessNumberCountry: values.businessNumberCountry,
    businessNumberVerified: isIndividual
      ? false
      : values.businessNumberVerified,
    categories: values.categories?.length ? values.categories : undefined,
    ...(sendContacts && contacts ? { contacts } : {}),
    entityName: values.entityName ?? '',
    entityType: values.entityType || undefined,
    gstStatus: values.gstStatus ?? undefined,
    // Tells the backend to notify the workspace owner about an unverified ABN.
    ...(isOnboarding ? { isOnboardingPage: true } : {}),
    // Empty string clears the stored value — legacy contact-form parity.
    ...(isOnboarding || dirtyFields.phone ? { phone: values.phone || '' } : {}),
    representsClients: isIndividual
      ? undefined
      : values.representsClients === 'yes',
    taxResidency: values.taxResidency || undefined,
    ...(isOnboarding || dirtyFields.website
      ? { website: values.website || '' }
      : {}),
    workspaceName: values.workspaceName || undefined,
    workspaceType:
      values.entityType &&
      (values.entityType === EntityTypeEnum.Individual ||
        values.representsClients !== '')
        ? workspaceTypeFromEntityType(
            values.entityType,
            values.representsClients,
          )
        : undefined,
  };
};
